import React, { useState, useEffect, useRef } from 'react';

import Grid from '@mui/material/Unstable_Grid2';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { useTheme } from '@mui/material/styles';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import OtherHousesIcon from '@mui/icons-material/OtherHouses';

import NumberFormat from 'react-number-format';
import Skeleton from 'react-loading-skeleton';
import Ajv from 'ajv';
import { withTranslation } from 'react-i18next';
import LazyLoad from 'react-lazyload';
import axios from 'axios';

import { useSchema } from '../utils/useSchema';
import { createHoaState, config } from '@homewise-solutions-inc/pkg-react-utils';

import hwLogo from '../img/homewise.png';
import nwLogo from '../img/partners/nerdwallet.png';
import PoweredBy from './PoweredBy';
import Loading from './Loading';

const cards = [1, 2, 3];

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
    />
  );
});

const types = [
  {
    value: 'purchase',
    label: 'Purchase',
  },
  {
    value: 'refinance',
    label: 'Refinance',
  },
  {
    value: 'switch',
    label: 'Renewal/Switch',
  },
];
const rateTypes = [
  {
    value: 'any',
    label: 'Any',
  },
  {
    value: 'fixed',
    label: 'Fixed',
  },
  {
    value: 'variable',
    label: 'Variable',
  },
];
const provinces = [
  {
    label: 'Alberta',
    value: 'AB',
  },
  {
    label: 'British Columbia',
    value: 'BC',
  },
  {
    label: 'Manitoba',
    value: 'MB',
  },
  {
    label: 'New Brunswick',
    value: 'NB',
  },
  {
    label: 'Newfoundland and Labrador',
    value: 'NL',
  },
  {
    label: 'Northwest Territories',
    value: 'NT',
  },
  {
    label: 'Nova Scotia',
    value: 'NS',
  },
  {
    label: 'Nunavut',
    value: 'NU',
  },
  {
    label: 'Ontario',
    value: 'ON',
  },
  {
    label: 'Prince Edward Island',
    value: 'PE',
  },
  {
    label: 'Saskatchewan',
    value: 'SK',
  },
  {
    label: 'Yukon Territory',
    value: 'YT',
  },
];
const lenderTypes = [
  {
    value: 'any',
    label: 'Any',
  },
  {
    value: 'bank',
    label: 'Bank',
  },
  {
    value: 'monoline',
    label: 'Monoline',
  },
  {
    value: 'credit_union',
    label: 'Credit Union',
  },
];
const mortgageTermOptions = [
  {
    value: 'any',
    label: 'Any',
  },
  {
    value: '1',
    label: '1 Year',
  },
  {
    value: '2',
    label: '2 Years',
  },
  {
    value: '3',
    label: '3 Years',
  },
  {
    value: '4',
    label: '4 Years',
  },
  {
    value: '5',
    label: '5 Years',
  },
  {
    value: '7',
    label: '7 Years',
  },
  {
    value: '10',
    label: '10 Years',
  },
];
const creditScoreOptions = [
  {
    label: 'Any',
    value: 'any',
  },
  {
    label: 'Above 650',
    value: '650_900',
  },
  {
    label: 'Below 650',
    value: '300_649',
  },
];

const truncate = (str) => {
  return str.length > 33 ? str.substring(0, 30) + '...' : str;
};

const Landing = (props) => {
  const { t, size } = props;
  const theme = useTheme();

  const isMdUp = size.width >= 1024;
  const gridColumns = size.width >= 1024 ? 12 : 4;

  const hwConfig = props?.config;

  const [handOff, setHandOff] = useState(false);
  const [showHandOff, setShowHandOff] = useState(false);

  const [errors, setErrors] = useState({});
  const [dpError, setDpError] = useState(false);
  const [borrowingError, setBorrowingError] = useState(false);
  const [bRateError, setBRateError] = useState(false);

  const schema = useSchema('search');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [hoaState, setHoaState] = useState({});

  const [type, setType] = useState(hwConfig?.productTable?.mortgageType);
  const [rateType, setRateType] = useState(hwConfig?.productTable?.rateType);
  const [province, setProvince] = useState(hwConfig?.productTable?.province || hwConfig?.productTable?.province);
  const [showRentalRates, setShowRentalRates] = useState(hwConfig?.productTable?.rental === 'filtered' ? true : false);

  const [price, setPrice] = useState(showRentalRates ? hwConfig?.productTable?.filtered?.price : hwConfig?.productTable?.mixed?.price);
  const [downpayment, setDownpayment] = useState(
    showRentalRates ? hwConfig?.productTable?.filtered?.downPayment : hwConfig?.productTable?.mixed?.downPayment
  );
  const [balance, setBalance] = useState(showRentalRates ? hwConfig?.productTable?.filtered?.balance : hwConfig?.productTable?.mixed?.balance);
  const [term, setTerm] = useState(hwConfig?.productTable?.term);
  const [lenderType, setLenderType] = useState(hwConfig?.productTable?.lenderType);
  const [lastUpdated, setLastUpdated] = useState(new Date().toISOString());
  const [creditScore, setCreditScore] = useState(hwConfig?.productTable?.creditScore || 'any');

  // const [moreOptionsToggle, setMoreOptionsToggle] = useState(lenderType !== 'any' || term !== 'any' ? true : false);
  const [moreOptionsToggle, setMoreOptionsToggle] = useState(true);

  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);

  useEffect(() => {
    setList([...products?.slice(0, 5)]);
  }, [products]);

  const [loadMore, setLoadMore] = useState(false);
  const [hasMore, setHasMore] = useState(products?.length > 10);

  const myRef = useRef(null);
  const newRef = useRef(null);

  /**
   * Custom event to handle search button click & dispatch the event
   * @param {Object} search_data
   */
  const handleSearchClick = () => {
    const search_data = {
      index: props.index,
      className: `homewise_product_table_${props.index}`,
      mortgageType: type,
      rateType: rateType,
      province: province,
      price: Number(price),
      downPayment: Number(downpayment),
      balance: Number(balance >= price ? 0 : balance),
      borrowing: type === 'purchase' ? Number(price - downpayment) : Number(balance >= price ? 0 : balance),
      isRental: showRentalRates,
      rentalToggle: hwConfig?.productTable?.rental,
      lenderType: lenderType,
      term: term,
      creditScore: creditScore,
      hide: hwConfig?.productTable?.hide,
      lenderName: hwConfig?.productTable?.lenderName,
    };

    const searchCustomEvent = new CustomEvent('hw_product_table_search_click', {
      detail: search_data,
    });

    window.dispatchEvent(searchCustomEvent);
  };

  /**
   * Custom event to handle product click & dispatch the event
   * @param {Object} product
   */
  const handleClick = (product) => {
    const productClickCustomEvent = new CustomEvent('hw_product_table_rate_click', {
      detail: {
        index: props.index,
        className: `homewise_product_table_${props.index}`,
        lender: product.lender,
        legal_name: product.legal_name,
        rate_value: product.rate_value,
        term: product.term,
        payment: product.payment,
      },
    });

    window.dispatchEvent(productClickCustomEvent);
  };

  const executeScroll = (ref) => {
    // console.log('scroll');
    ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const executeClick = (ref) => {
    // console.log('click');
    ref.current.click();
  };

  const delayAndGo = (e, path, item) => {
    e.preventDefault();

    handleClick(item);

    setShowHandOff(true);
    setTimeout(() => {
      executeScroll(myRef);
      newRef.current.href = path;
    }, 100);
    setTimeout(() => {
      executeClick(newRef);
    }, 3000);
  };

  const renderProducts = list?.map((item, index) => {
    function configureLink() {
      try {
        // Following logic removes the trailing slashes '/' from the path (preserves all other URL properties like querystrings).
        const url = hwConfig?.productTable?.link
          ?.toString()
          .replace(/(?:\/+(\?))/, '$1')
          .replace(/\/+$/, '');

        let path = [url];

        const urlObject = new URL(url);

        let isPartnerTypeSet = urlObject?.pathname
          ?.split('/')
          ?.filter((e) => e !== '' && e !== undefined)
          ?.includes(hwConfig?.partner?.type);

        let isPartnerCodeSet = urlObject?.pathname
          ?.split('/')
          ?.filter((e) => e !== '' && e !== undefined)
          ?.includes(hwConfig?.partner?.code);

        let isPartnerSegmentSet = urlObject?.pathname
          ?.split('/')
          ?.filter((e) => e !== '' && e !== undefined)
          ?.includes(hwConfig?.partner?.segment);

        // All logic applies when the link is pointing to our domain.
        if (url?.includes('my.thinkhomewise.com')) {
          if (!isPartnerTypeSet && !!hwConfig?.partner?.type && !url?.includes('?'))
            path.push(`/${hwConfig?.partner?.type}`), (isPartnerTypeSet = true);

          if (isPartnerTypeSet && !isPartnerCodeSet && !!hwConfig?.partner?.code && !url?.includes('?'))
            path.push(`/${hwConfig?.partner?.code}`), (isPartnerCodeSet = true);

          if (isPartnerTypeSet && isPartnerCodeSet && !isPartnerSegmentSet && !!hwConfig?.partner?.segment && !url?.includes('?'))
            path.push(`/${hwConfig?.partner?.segment}`);

          if (!urlObject?.searchParams?.has('hw_product')) {
            if (url?.includes('?')) path.push('&');
            else path.push('?');
            path.push(`hw_product=${item?.id}`);
          }
        }

        // When there is a custom link specified in partner config.
        else {
          if (url?.includes('hw_product=')) path.push(`${item.id}`);
          else {
            if (url?.includes('?')) path.push('&');
            else path.push('?');
            path.push('hw_product'), path.push(`=${item.id}`);
          }

          if (!isPartnerSegmentSet && !!hwConfig?.partner?.segment) path.push(`&hw_segment=${hwConfig?.partner?.segment}`);
        }

        if (url?.includes('my.thinkhomewise.com')) path.push('&state');
        else path.push('&hw_state');

        path.push(`=${encodeURIComponent(hoaState)}`);

        // append all other query params of the current URL to the path
        if (window?.location?.search) {
          const searchParams = new URLSearchParams(window?.location?.search);
          path.push(`&${searchParams.toString()}`);
        }

        // if hwConfig?.productTable?.linkParameters exists { a: b, c: d } then append them to the path as query params
        if (hwConfig?.productTable?.linkParameters && Object.keys(hwConfig?.productTable?.linkParameters).length > 0) {
          Object.keys(hwConfig?.productTable?.linkParameters).map((key) => {
            path.push(`&${key}=${hwConfig?.productTable?.linkParameters[key]}`);
          });
        }

        // if hwConfig?.productTable?.linkFragments exists { a: b, c: d } then append them to the path as #fragments
        if (hwConfig?.productTable?.linkFragments && Object.keys(hwConfig?.productTable?.linkFragments).length > 0) {
          path.push('#');
          Object.keys(hwConfig?.productTable?.linkFragments).map((key) => {
            path.push(`${key}=${hwConfig?.productTable?.linkFragments[key]}`);
          });
        }

        return path
          .join('')
          .replace(/(?:\/+(\?))/, '$1')
          .replace(/\/+$/, '');
      } catch (e) {
        console.log('configureLink error ->> ', e);
        return 'https://my.thinkhomewise.com/';
      }
    }

    return (
      <React.Fragment key={index}>
        {hwConfig?.productTable?.style === 'card' ? (
          <Grid xs={12}>
            <Card
              className="hw__card"
              style={{
                border: `1px solid ` + hwConfig.productTable.primaryColor + 80,
              }}
            >
              <CardContent className="hw__content">
                <div className="hw__logo">
                  <LazyLoad height={100} offset={100}>
                    <div style={{ margin: `0 auto`, padding: `0 1rem` }}>
                      <img src={item.logo} alt="" width="240" className="hw__logo-image" style={{ width: `80%`, objectFit: `cover` }} />
                    </div>
                  </LazyLoad>
                  <small className="hw__product-name" style={{ display: `block`, margin: `0` }}>
                    {item.legal_name}
                    {hwConfig?.productTable?.productName && (
                      <>
                        <br />({item.lender_product})
                      </>
                    )}
                  </small>
                </div>

                <div className="hw__details">
                  <Container maxWidth="lg">
                    <Grid container spacing={2} className="hw__details-container">
                      {hwConfig?.productTable?.featureCount !== 0 && size.width >= 1024 && (
                        <Grid md={6} className="hw__features" style={{ paddingLeft: `1rem`, margin: `0 1rem` }}>
                          <ul>
                            {item.highlight.slice(0, hwConfig?.productTable?.featureCount).map((feature, index) => (
                              <li key={index}>
                                <span>{truncate(feature)}</span>
                              </li>
                            ))}
                          </ul>
                        </Grid>
                      )}
                      <Grid md className="hw__rate">
                        <p className="hw__h2">{item.rate_value}%</p>
                        <p>
                          <small>
                            {item.term} yrs. {item.rateType}
                          </small>
                        </p>
                      </Grid>
                      <Grid md className="hw__monthly">
                        <NumberFormat
                          value={item.payment}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={'$'}
                          renderText={(value, props) => (
                            <p className="hw__h2" {...props}>
                              {value}
                            </p>
                          )}
                        />
                        <p>
                          <small>per month</small>
                        </p>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </CardContent>
              <CardActions className="hw__action">
                {handOff ? (
                  <Button size="large" color="primary" variant="contained" onClick={(e) => delayAndGo(e, configureLink())}>
                    {hwConfig?.productTable?.cta ? hwConfig?.productTable?.cta : 'Get This Mortgage'}
                  </Button>
                ) : (
                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    componet="a"
                    href={configureLink()}
                    onClick={() => handleClick(item)}
                    target="_blank"
                  >
                    {hwConfig?.productTable?.cta ? hwConfig?.productTable?.cta : 'Get This Mortgage'}
                  </Button>
                )}

                {hwConfig?.productTable?.accreditation && <PoweredBy />}
              </CardActions>

              <Accordion className="hw__accordion" elevation={0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                  Details
                </AccordionSummary>
                <AccordionDetails>
                  <ul className="hw__accordion-ul">
                    {item.highlight.slice(0, 2).map((feature, index) => (
                      <li key={index}>
                        <span>{truncate(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </AccordionDetails>
              </Accordion>
            </Card>
          </Grid>
        ) : isMdUp ? (
          <TableBody className="hw__table-body">
            <TableRow>
              <TableCell
                scope="row"
                align="center"
                sx={{
                  borderBottom: {
                    xs: `0`,
                    md: `1px solid rgba(224, 224, 224, 1)`,
                  },
                  maxWidth: `300px`,
                }}
              >
                <LazyLoad height={100} offset={100}>
                  <div style={{ margin: `0 auto`, padding: `0 1rem` }}>
                    <img src={item.logo} alt="" width="240" className="hw__logo-image" style={{ width: `80%`, objectFit: `cover` }} />
                  </div>
                </LazyLoad>
                <h3 className="hw__legal-name">{item.legal_name}</h3>
                {hwConfig?.productTable?.productName &&(
                  <small className="hw__product-name">{item.lender_product}</small>
                )}
              </TableCell>

              {hwConfig?.productTable?.featureCount !== 0 && (
                <TableCell className="hw__features" scope="row">
                  <ul style={{ paddingLeft: `0` }}>
                    {item.highlight.slice(0, hwConfig?.productTable?.featureCount).map((feature, index) => (
                      <li key={index}>
                        <p style={{ margin: 0 }}>{feature}</p>
                      </li>
                    ))}
                  </ul>
                </TableCell>
              )}

              <TableCell className="hw__rate" scope="row">
                <h4
                  style={{
                    fontSize: `1.25rem`,
                    justifyContent: `center`,
                    display: 'flex',
                  }}
                >
                  {item.rate_value}
                  {item.rate_raw !== 'tbd' && '%'}
                </h4>
                <p style={{ display: `flex`, justifyContent: `center` }}>
                  <small>
                    {item.rate_type}
                    {item.rental && hwConfig?.productTable?.rental === 'mixed' && ', '}
                  </small>
                  {item.rental && hwConfig?.productTable?.rental === 'mixed' && (
                    <>
                      <Tooltip title="Rental Rate" arrow>
                        <OtherHousesIcon
                          sx={{
                            margin: {
                              xs: `-4px 0 0 0`,
                            },
                            color: `#4c5257`,
                            height: `20px`,
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                </p>
              </TableCell>
              <TableCell className="hw__term" scope="row">
                <h4
                  style={{
                    fontSize: `1.25rem`,
                    justifyContent: `center`,
                    display: 'flex',
                  }}
                >
                  <span>{item.term} yrs.</span>
                </h4>
                <p style={{ display: `flex`, justifyContent: `center` }}>
                  <small>&nbsp;</small>
                </p>
              </TableCell>
              <TableCell className="hw__payment" scope="row">
                {item.rate_raw === 'tbd' ? (
                  <h4
                    style={{
                      fontSize: `1.25rem`,
                      justifyContent: `center`,
                      display: 'flex',
                    }}
                  >
                    {item.payment}
                  </h4>
                ) : (
                  <NumberFormat
                    value={item.payment}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={item.rate_raw !== 'tbd' ? '$' : ''}
                    renderText={(value, props) => (
                      <h4
                        style={{
                          fontSize: `1.25rem`,
                          justifyContent: `center`,
                          display: 'flex',
                        }}
                        {...props}
                      >
                        {value}
                      </h4>
                    )}
                  />
                )}
                <p style={{ display: `flex`, justifyContent: `center` }}>
                  <small>per month</small>
                </p>
              </TableCell>
              <TableCell className="hw__action" align="center" scope="row">
                {handOff ? (
                  <Button size="large" color="primary" variant="contained" onClick={(e) => delayAndGo(e, configureLink(), item)}>
                    {hwConfig?.productTable?.cta ? hwConfig?.productTable?.cta : 'Get This Mortgage'}
                  </Button>
                ) : (
                  <Button
                    size="large"
                    color="primary"
                    variant="contained"
                    componet="a"
                    href={configureLink()}
                    onClick={() => handleClick(item)}
                    target="_blank"
                  >
                    {hwConfig?.productTable?.cta ? hwConfig?.productTable?.cta : 'Get This Mortgage'}
                  </Button>
                )}

                {hwConfig?.productTable?.accreditation && <PoweredBy textAlign="center" />}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <Grid xs={12} className="hw__table-mobile">
            <TableContainer
              component={Paper}
              style={{ boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)` }}
            >
              <Table aria-label="simple table">
                <TableBody className="hw__table-body-mobile">
                  <TableRow>
                    <TableCell
                      className="hw__table-mobile-lender"
                      sx={{
                        width: '50%',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '24px', // Distance from the top
                          bottom: '24px', // Distance from the bottom
                          right: 0,
                          width: '1px',
                          backgroundColor: 'lightgrey',
                        },
                      }}
                      scope="row"
                      align="center"
                    >
                      <LazyLoad height={100} offset={100}>
                        <div style={{ margin: `0 auto` }}>
                          <img src={item.logom} alt="" width="150" className="hw__logo-image" style={{ width: `70%` }} />
                        </div>
                      </LazyLoad>
                      <small className="hw__product-name" style={{ display: `block`, marginBottom: hwConfig?.productTable?.accreditation ? `3rem` : `0` }}>
                        {item.legal_name}
                        {hwConfig?.productTable?.productName && (
                          <>
                            <br />({item.lender_product})
                          </>
                        )}
                      </small>
                      {hwConfig?.productTable?.accreditation && <PoweredBy textAlign="center" />}
                    </TableCell>
                    <TableCell className="hw__table-mobile-action" sx={{ width: '50%' }} scope="row" align="center">
                      <p
                        style={{
                          fontSize: `2rem`,
                          fontWeight: `700`,
                          margin: `5px 0 0`,
                          justifyContent: `center`,
                          display: item.rate_raw !== 'tbd' ? 'block' : 'flex',
                          color: hwConfig?.productTable?.primaryColor,
                        }}
                      >
                        {item.rate_value}
                        {item.rate_raw !== 'tbd' && '%'}
                      </p>
                      <p style={{ margin: `-.5rem 0 1rem` }}>
                        <small>
                          {item.term} yrs. {item.rate_type}
                        </small>
                        {item.rental && hwConfig?.productTable?.rental === 'mixed' && (
                          <Tooltip title="Rental Rate" arrow>
                            <OtherHousesIcon
                              sx={{
                                margin: {
                                  xs: `.4rem 0 -.2rem 0`,
                                },
                                color: `#4c5257`,
                                height: `20px`,
                              }}
                            />
                          </Tooltip>
                        )}
                      </p>

                      {handOff ? (
                        <Button size="small" color="primary" variant="contained" onClick={(e) => delayAndGo(e, configureLink(), item)}>
                          {hwConfig?.productTable?.cta ? hwConfig?.productTable?.cta : 'Get This Mortgage'}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          componet="a"
                          href={configureLink()}
                          onClick={() => handleClick(item)}
                          target="_blank"
                        >
                          {hwConfig?.productTable?.cta ? hwConfig?.productTable?.cta : 'Get This Mortgage'}
                        </Button>
                      )}
                      <p style={{ marginTop: `4px` }}>
                        <small>
                          <NumberFormat
                            value={item.payment}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                            renderText={(value) => value}
                          />{' '}
                          per month
                        </small>
                      </p>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </React.Fragment>
    );
  });

  // Load more button click
  const handleLoadMore = () => {
    setLoadMore(true);
  };

  // Handle loading more articles
  useEffect(() => {
    if (loadMore && hasMore) {
      const currentLength = list.length;
      const isMore = currentLength < products.length;
      const nextResults = isMore ? products.slice(currentLength, currentLength + 3) : [];
      setList([...list, ...nextResults]);
      setLoadMore(false);
    }
  }, [loadMore, hasMore]); //eslint-disable-line

  //Check if there is more
  useEffect(() => {
    const isMore = list.length < products.length;
    setHasMore(isMore);
  }, [list]);

  const errorsForHumans = (item) => {
    const path = item.instancePath ? item.instancePath.split('/') : item.dataPath.split('/');
    const target = path[path.length - 1];
    setErrors((errors) => ({
      ...errors,
      [target]: {
        invalid: true,
        error: t('errors:main.' + [target] + '.' + item.keyword),
      },
    }));
  };

  const schemaValidate = (schema, data) => {
    setErrors([]);

    const ajv = Ajv({
      allErrors: true,
      jsonPointers: true,
      verbose: true,
    });

    let validate = ajv.compile(schema);
    let valid = validate(data);
    if (!valid) {
      validate.errors.map((item, i) => {
        return errorsForHumans(item);
      });
      return true;
    }
  };

  const fetchProducts = async (rentalRates = showRentalRates) => {
    setBorrowingError(false);
    setDpError(false);
    setBRateError(false);
    setErrors([]);

    // Check B-rate minimum down payment
    if (creditScore === '300_649' && type === 'purchase') {
      const downPaymentPercentage = ((downpayment / price) * 100).toFixed(3);
      if (downPaymentPercentage < 20) {
        setBRateError(true);
      } else {
        setBRateError(false);
      }
    }

    if (type !== 'purchase') {
      if (balance >= price) setBorrowingError(true);
      else setBorrowingError(false);
    } else setBorrowingError(false);

    let search_data = {
      mortgageType: type,
      rateType: rateType,
      province: province,
      price: Number(price),
      downPayment: Number(downpayment),
      balance: Number(balance >= price ? 0 : balance),
      borrowing: type === 'purchase' ? Number(price - downpayment) : Number(balance >= price ? 0 : balance),
      isRental: rentalRates,
      rentalToggle: hwConfig?.productTable?.rental,
      lenderType: lenderType,
      term: term,
      creditScore: creditScore,
      hide: hwConfig?.productTable?.hide,
      lenderName: hwConfig?.productTable?.lenderName,
    };

    if (type === 'switch') delete search_data.downPayment;

    if (rentalRates) {
      const equityPercentage = (((price - search_data.borrowing) / price) * 100).toFixed(3);

      if (equityPercentage < 20) setDpError(true);
      else setDpError(false);
    } else setDpError(false);

    const err = schemaValidate(schema, search_data);

    if (!err && !borrowingError && !dpError && !bRateError) {
      setLoading(true);
      setProducts([]);
      setTotal(0);

      try {
        const response = await axios.post(`${config.api.BASE}/product/search`, search_data);
        const data = response.data;

        setLastUpdated(response?.headers['x-updated-at']);
        setLoading(false);

        if (data.errors) data.errors.map((item) => errorsForHumans(item));
        else {
          setProducts(data?.rates);
          setTotal(data?.rates?.length);
          setHoaState(createHoaState(search_data));
        }
      } catch (e) {
        console.log('Something went wrong while fetching products', e);
        setLoading(false);
        setProducts([]);
        setHoaState({});
      }
    } else {
      setLoading(false);
      console.table(errors);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleChange = (event) => {
    setType(event.target.value);
    setDpError(false);
    setBorrowingError(false);
  };
  const changeRateType = (event) => {
    setRateType(event.target.value);
  };
  const changeProvince = (event) => {
    setProvince(event.target.value);
  };
  const changePrice = (event) => {
    setPrice(event.target.value);
  };
  const changeDownpayment = (event) => {
    setDownpayment(event.target.value);
  };
  const changeBalance = (event) => {
    setBalance(Number(event.target.value));
  };
  const changeLenderType = (event) => {
    setLenderType(event.target.value);
  };
  const changeTerm = (event) => {
    setTerm(event.target.value);
  };
  const changeCreditScore = (event) => {
    setBRateError(false);
    setCreditScore(event.target.value);
  };

  let termsUrl = 'https://thinkhomewise.com/page/terms',
    appUrl = 'https://my.thinkhomewise.com',
    partnerPath;

  if (!!hwConfig?.partner?.type && !!hwConfig?.partner?.code) partnerPath = `/${hwConfig?.partner?.type}/${hwConfig?.partner?.code}`;

  if (partnerPath && !!hwConfig?.partner?.segment) partnerPath = `${partnerPath}/${hwConfig?.partner?.segment}`;

  if (!!hwConfig?.partner) {
    termsUrl = !!hwConfig.partner.type && !!hwConfig.partner.code ? `${termsUrl}/#${partnerPath}` : termsUrl;

    appUrl = !!hwConfig.partner.type && !!hwConfig.partner.code ? `${appUrl}${partnerPath}` : appUrl;
  }

  const showLenderType = !hwConfig?.productTable?.hide?.includes('lenderType');
  const showTerm = !hwConfig?.productTable?.hide?.includes('term');
  const showCreditScore = !hwConfig?.productTable?.hide?.includes('creditScore');
  const showAllThree = showLenderType && showTerm && showCreditScore;

  return (
    <>
      <div
        style={{
          backgroundColor: hwConfig?.productTable?.bg,
        }}
        className={hwConfig?.productTable?.font ? '_fonts_native' : ''}
        id={size.width >= 1024 ? 'homewise_product_table_d' : 'homewise_product_table_m'}
      >
        <main>
          <div>
            <Container maxWidth="lg">
              <Grid xs={12}>
                {hwConfig.productTable.intro && (
                  <>
                    <h1>Compare current mortgage rates for today</h1>
                    <p>
                      It pays to compare multiple lenders to help you negotiate, find the right deal for you and save in the long run. Get a feel for
                      your top picks to start the process of finding the lender for&nbsp;you.
                    </p>
                  </>
                )}
                <p>
                  <small>Rates updated: {new Date(lastUpdated).toDateString()}</small>
                </p>
              </Grid>
            </Container>
          </div>

          <Container maxWidth="lg">
            <Grid container spacing={2} columns={gridColumns} sx={{ justifyItems: `center` }}>
              <Grid xs={size.width >= 1024 ? (type === '' || null ? 4 : 2) : 12}>
                <TextField
                  id="mortgageType"
                  fullWidth
                  select
                  label="Mortgage Type"
                  value={type}
                  onChange={handleChange}
                  error={errors.mortgageType ? errors.mortgageType.invalid : false}
                  helperText={errors.mortgageType ? errors.mortgageType.error : ''}
                  InputLabelProps={{
                    style: { color: '#4c5257' },
                  }}
                >
                  {types.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid xs={2}>
                <TextField
                  id="price"
                  fullWidth
                  label={type === `switch` || type === `refinance` ? `Property Value` : `Purchase Price`}
                  variant="outlined"
                  InputProps={{
                    inputComponent: NumberFormatCustom,
                    inputProps: { maxLength: 9 },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    style: { color: '#4c5257' },
                  }}
                  placeholder={`0`}
                  value={price}
                  onChange={changePrice}
                  error={errors.price ? errors.price.invalid : false}
                  helperText={errors.price ? errors.price.error : ''}
                />
              </Grid>
              {(type === `switch` || type === `refinance`) && (
                <Grid xs={2}>
                  <TextField
                    id="balance"
                    fullWidth
                    label={`Mortgage Balance`}
                    variant="outlined"
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      inputProps: { maxLength: 9 },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: { color: '#4c5257' },
                    }}
                    placeholder={`0`}
                    value={balance}
                    onChange={changeBalance}
                    error={errors.balance ? errors.balance.invalid : false}
                    helperText={errors.balance ? errors.balance.error : ''}
                  />
                </Grid>
              )}
              {type === `purchase` && (
                <Grid xs={2}>
                  <TextField
                    id="downPayment"
                    fullWidth
                    label="Down Payment"
                    variant="outlined"
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      inputProps: { maxLength: 9 },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      style: { color: '#4c5257' },
                    }}
                    placeholder={`0`}
                    value={downpayment}
                    onChange={changeDownpayment}
                    error={errors.downPayment ? errors.downPayment.invalid : false}
                    helperText={errors.downPayment ? errors.downPayment.error : ''}
                  />
                </Grid>
              )}
              <Grid xs={2}>
                <TextField
                  id="rateType"
                  fullWidth
                  select
                  label="Rate Type"
                  value={rateType}
                  onChange={changeRateType}
                  error={errors.rateType ? errors.rateType.invalid : false}
                  helperText={errors.rateType ? errors.rateType.error : ''}
                  InputLabelProps={{
                    style: { color: '#4c5257' },
                  }}
                >
                  {rateTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid xs={2}>
                <TextField
                  id="province"
                  fullWidth
                  select
                  label="Province"
                  value={province}
                  onChange={changeProvince}
                  error={errors.province ? errors.province.invalid : false}
                  helperText={errors.province ? errors.province.error : ''}
                  InputLabelProps={{
                    style: { color: '#4c5257' },
                  }}
                >
                  {provinces.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid xs={size.width >= 1024 ? 2 : 12} alignSelf="center">
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  sx={{ width: `100%`, marginBottom: size.width >= 1024 ? `0` : `1rem` }}
                  onClick={() => {
                    fetchProducts();
                    handleSearchClick();
                  }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
            {moreOptionsToggle && (
              <Grid container spacing={2} columns={gridColumns} sx={{ marginTop: size.width >= 1024 ? `1rem` : `0`, justifyItems: `center` }}>
                {showLenderType && (
                  <Grid xs={!showAllThree ? 6 : 4}>
                    <TextField
                      id="lenderType"
                      fullWidth
                      select
                      label="Lender Type"
                      value={lenderType}
                      onChange={changeLenderType}
                      error={errors.lenderType ? errors.lenderType.invalid : false}
                      helperText={errors.lenderType ? errors.lenderType.error : ''}
                      InputLabelProps={{
                        style: { color: '#4c5257' },
                      }}
                    >
                      {lenderTypes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {showTerm && (
                  <Grid xs={!showAllThree ? 6 : 4}>
                    <TextField
                      id="term"
                      fullWidth
                      select
                      label="Mortgage Term"
                      value={term}
                      onChange={changeTerm}
                      error={errors.term ? errors.term.invalid : false}
                      helperText={errors.term ? errors.term.error : ''}
                      InputLabelProps={{
                        style: { color: '#4c5257' },
                      }}
                    >
                      {mortgageTermOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {showCreditScore && (
                  <Grid xs={!showAllThree ? 6 : 4}>
                    <TextField
                      id="creditScore"
                      fullWidth
                      select
                      label="Credit Score"
                      value={creditScore}
                      onChange={changeCreditScore}
                      error={errors.creditScore ? errors.creditScore.invalid : false}
                      helperText={errors.creditScore ? errors.creditScore.error : ''}
                      InputLabelProps={{
                        style: { color: '#4c5257' },
                      }}
                    >
                      {creditScoreOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            )}

            {borrowingError && (
              <Grid container justifyContent="center" sx={{ textAlign: `center`, padding: `1rem 0 1rem 0` }}>
                <Alert
                  severity="warning"
                  sx={{
                    background: `rgba(248, 143, 6, .1)`,
                    border: `1px solid rgb(248, 143, 6)`,
                    borderRadius: `5px`,
                    color: `rgb(248, 143, 6)`,
                    // display: `flex`,
                    alignItems: `center`,
                    justify: `center`,
                    fontSize: `.8rem`,
                    padding: `.5rem`,
                  }}
                >
                  Mortgage balance cannot exceed the property value.
                </Alert>
              </Grid>
            )}

            {dpError && (
              <Grid container justifyContent="center" sx={{ textAlign: `center`, padding: `1rem 0 1rem 0` }}>
                <Alert
                  severity="warning"
                  sx={{
                    background: `rgba(248, 143, 6, .1)`,
                    border: `1px solid rgb(248, 143, 6)`,
                    borderRadius: `5px`,
                    color: `rgb(248, 143, 6)`,
                    // display: `flex`,
                    alignItems: `center`,
                    justify: `center`,
                    fontSize: `.8rem`,
                    padding: `.5rem`,
                  }}
                >
                  {type === 'purchase' ? 'Your down payment' : `Your property's equity`} must be at least 20% for investment/rental loans.
                </Alert>
              </Grid>
            )}

            {bRateError && (
              <Grid container justifyContent="center" sx={{ textAlign: `center`, padding: `1rem 0 1rem 0` }}>
                <Alert
                  severity="warning"
                  sx={{
                    background: `rgba(248, 143, 6, .1)`,
                    border: `1px solid rgb(248, 143, 6)`,
                    borderRadius: `5px`,
                    color: `rgb(248, 143, 6)`,
                    alignItems: `center`,
                    justify: `center`,
                    fontSize: `.8rem`,
                    padding: `.5rem`,
                  }}
                >
                  Your down payment must be at least 20% for alternate rates.
                </Alert>
              </Grid>
            )}

            <Grid container spacing={2} alignItems="center">
              {!loading ? (
                <>
                  {total > 0 ? (
                    <Grid xs={12} sm={8}>
                      <p>
                        <strong>
                          Here {total === 1 ? <>is</> : <>are</>} <span>{total}</span> option
                          {total === 1 ? <></> : <>s</>} for you
                        </strong>
                      </p>
                    </Grid>
                  ) : (
                    <Grid xs={hwConfig?.productTable?.rental === 'mixed' ? 6 : 12} sm={hwConfig?.productTable?.rental === 'mixed' ? 8 : 12}>
                      <p
                        style={{
                          textAlign: hwConfig?.productTable?.rental === 'mixed' ? `left` : `center`,
                          padding: `0 1rem 1rem 0rem`,
                        }}
                      >
                        <strong>Hmm, we didn't find any mortgage options for that selection. Try tweaking your filters!</strong>
                      </p>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid xs={6} sm={8}>
                  <p>
                    <Skeleton />
                  </p>
                </Grid>
              )}
              {hwConfig?.productTable?.rental === 'mixed' && (
                <Grid
                  xs={12}
                  sm={4}
                  sx={{
                    textAlign: `right`,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showRentalRates}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setShowRentalRates(isChecked);
                          fetchProducts(isChecked);
                        }}
                        name="showAll"
                        color="primary"
                      />
                    }
                    labelPlacement="start"
                    label={
                      <>
                        <OtherHousesIcon
                          sx={{
                            verticalAlign: `middle`,
                            padding: `0 .5rem .3rem 0`,
                          }}
                        />
                        <p style={{ marginBlock: `0`, display: `inline` }}>Rental Rates</p>
                      </>
                    }
                  />
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2}>
              {loading &&
                cards.map((card) => (
                  <Grid key={card + '-hwc'} xs={12}>
                    <Card className="hw__card">
                      <CardContent className="hw__content">
                        <div className="hw__logo">
                          <Skeleton height={`100px`} />
                        </div>

                        <div className="hw__details">
                          <div className="hw__features" style={{ paddingLeft: `1rem` }}>
                            <h3>
                              <Skeleton width={`150px`} />
                            </h3>
                            <p>
                              <Skeleton width={`100px`} />
                            </p>
                          </div>
                          <div className="hw__rate">
                            <h3>
                              <Skeleton width={`110px`} />
                            </h3>
                            <p style={{ paddingTop: `1rem` }}>
                              <Skeleton width={`100px`} />
                            </p>
                          </div>
                          <div className="hw__monthly">
                            <h3>
                              <Skeleton width={`110px`} />
                            </h3>
                            <p style={{ paddingTop: `1rem` }}>
                              <Skeleton width={`100px`} />
                            </p>
                          </div>
                        </div>
                      </CardContent>
                      <CardActions className="hw__action">
                        <Skeleton height={`50px`} width={`100px`} />
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              {!loading &&
                products?.length > 0 &&
                (hwConfig?.productTable?.style === 'card' ? (
                  renderProducts
                ) : isMdUp ? (
                  <Grid xs={12}>
                    <TableContainer
                      component={Paper}
                      style={{ boxShadow: `0px 1px 0px #EBEBEB, -12px 24px 21px rgba(0, 0, 0, 0.03), 12px 24px 21px rgba(0, 0, 0, 0.03)` }}
                    >
                      <Table aria-label="simple table">
                        <TableHead align="center" className="hw__table-header">
                          <TableRow>
                            <TableCell>Lender</TableCell>
                            {hwConfig?.productTable?.featureCount !== 0 && <TableCell>Features</TableCell>}
                            <TableCell>Rate</TableCell>
                            <TableCell>Term</TableCell>
                            <TableCell>Payment</TableCell>
                            <TableCell align="right"> </TableCell>
                          </TableRow>
                        </TableHead>
                        {renderProducts}
                      </Table>
                    </TableContainer>
                  </Grid>
                ) : (
                  renderProducts
                ))}
            </Grid>

            <Grid container spacing={2}>
              {total > 0 && (
                <Grid
                  xs={12}
                  justifyItems="center"
                  style={{
                    display: `flex`,
                    justifyContent: `center`,
                    padding: `2rem 0`,
                  }}
                >
                  {hasMore && (
                    <Button color="primary" variant="contained" onClick={handleLoadMore}>
                      More
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>
          </Container>
        </main>
        <footer className="hw__ftr">
          <Container maxWidth="lg">
            <p>
              <small>
                These rates do not include taxes, fees, and insurance. Your actual rate and loan terms will be determined by the partner’s assessment
                of your creditworthiness and other factors. Any potential savings figures are estimates based on the information provided by you and
                our advertising partners. Advertiser disclosure: The listings on this page feature lenders who have a relationship with Homewise. If
                you choose to use Homewise's services, we will receive a referral fee. Listings are arranged by rates, from lowest to highest, or
                by&nbsp;recommendation.
              </small>
            </p>
            <p>
              <small>
                Mortgage Brokerage Licensed in ON #12984, AB #00516872, BC #X301004, MB, NS #2022-3000058, NL #23-07-HO061-1, NB #10057266, SK
                #512263. Homewise can pursue mortgage brokering activity across Canada. We are proudly Canadian!
              </small>
            </p>
            <div className="hw__ftr-sm">
              <ul>
                <li>
                  <p>
                    <small>
                      <a href={termsUrl} target="_blank" className="hw__ftr-link">
                        Terms &amp; Conditions
                      </a>
                    </small>
                  </p>
                </li>
              </ul>
              <PoweredBy url={appUrl} />
            </div>
          </Container>
        </footer>
      </div>
      {showHandOff && (
        <div className={hwConfig?.productTable?.font ? '_fonts_native hw__takeOver' : 'hw__takeOver'}>
          <div ref={myRef} className="hw__takeOver_content">
            <LazyLoad height={35} offset={100} once>
              <img src={nwLogo} height="35" alt="NerdWallet" /> <AddIcon />{' '}
            </LazyLoad>
            <LazyLoad height={35} offset={100} once>
              <img src={hwLogo} height="35" alt="Homewise" />
            </LazyLoad>
            <p>One moment, handing you off to Homewise...</p>
            <Loading />
            <a href={''} target="_blank" className="hw__hidden" ref={newRef} onClick={() => setShowHandOff(false)}>
              Go Now
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default withTranslation('errors')(Landing);
